import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Default timezone for the dashboard (Central/Chicago)
const DEFAULT_TIMEZONE = "America/Chicago";

const isCalendarDate = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

/** Backend storage datetimes are UTC even when their timezone suffix is omitted. */
export function parseDashboardTimestamp(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }
  if (typeof value !== 'string') return null;
  let normalized = value.trim();
  const storageDatePattern = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:[zZ]|[+-]\d{2}:?\d{2})?)?$/;
  if (!storageDatePattern.test(normalized)) return null;
  const [year, month, day] = normalized.slice(0, 10).split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()) {
    return null;
  }
  if (isCalendarDate(normalized)) normalized += 'T00:00:00';
  normalized = normalized.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1').replace(/z$/, 'Z');
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  if (!/(?:Z|[+-]\d{2}:\d{2})$/.test(normalized)) normalized += 'Z';
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatRecordTimestamp(value, tz, options = {}) {
  const parsed = parseDashboardTimestamp(value);
  if (!parsed) return 'Not available';
  const calendarOnly = isCalendarDate(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(calendarOnly ? {} : { hour: 'numeric', minute: '2-digit', timeZoneName: 'shortOffset' }),
    ...(calendarOnly ? {} : options),
    timeZone: calendarOnly ? 'UTC' : getValidTimezone(tz),
  }).format(parsed).replace('GMT', 'UTC');
}

/**
 * Get UTC offset string from IANA timezone identifier
 * @param {string} timezone - IANA timezone identifier (e.g., "America/Chicago", "Europe/Madrid")
 * @returns {string} UTC offset string (e.g., "UTC-6", "UTC+1")
 */
export function getTimezoneOffset(tz, timestamp) {
  if (!tz) return "";
  try {
    // Create a date object and format it with the timezone
    const date = timestamp == null ? new Date() : parseDashboardTimestamp(timestamp);
    if (!date) return '';
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");
    if (offsetPart) {
      // Format is like "GMT-6" or "GMT+1", convert to "UTC-6" or "UTC+1"
      return offsetPart.value.replace("GMT", "UTC");
    }
    return "";
  } catch (e) {
    return "";
  }
}

/**
 * Validate if a timezone string is a valid IANA timezone identifier
 * @param {string} tz - Timezone to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidTimezone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    // Try to use the timezone with Intl API - will throw if invalid
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get a valid timezone, falling back to default if the provided one is invalid
 * @param {string} tz - Timezone to validate
 * @returns {string} Valid timezone (either the provided one or the default)
 */
export function getValidTimezone(tz, defaultTz = DEFAULT_TIMEZONE) {
  if (isValidTimezone(tz)) {
    return tz;
  }
  // Try to get the browser's timezone as a more relevant fallback
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz) console.warn(`Invalid timezone "${tz}", falling back to ${browserTimezone || defaultTz}`);
  return browserTimezone || defaultTz;
}

/**
 * Format a UTC timestamp to the specified timezone
 * @param {string} timestamp - UTC timestamp (e.g., "2025-12-17T14:23:00.000Z")
 * @param {string} tz - IANA timezone identifier (e.g., "America/Chicago", "Europe/Madrid")
 * @param {string} format - Output format (default: "MM-DD-YYYY")
 * @returns {string} Formatted date string in the specified timezone
 */
export function formatTimestampWithTimezone(
  timestamp,
  tz = DEFAULT_TIMEZONE,
  format = "MM-DD-YYYY"
) {
  if (!timestamp) return "";
  try {
    const utcTimestamp = parseDashboardTimestamp(timestamp);
    if (!utcTimestamp) return '';
    // Validate timezone and fall back to default if invalid
    const validTz = isCalendarDate(timestamp) ? 'UTC' : getValidTimezone(tz);
    const date = dayjs(utcTimestamp).tz(validTz);
    return date.format(format);
  } catch (error) {
    console.error("Error formatting timestamp with timezone:", error);
    return "";
  }
}

/**
 * Format a UTC timestamp to the specified timezone with time included
 * @param {string} timestamp - UTC timestamp (e.g., "2025-12-17T14:23:00.000Z")
 * @param {string} tz - IANA timezone identifier (e.g., "America/Chicago", "Europe/Madrid")
 * @returns {string} Formatted date string with time in the specified timezone (MM-DD-YYYY, h:mm a)
 */
export function formatTimestampWithTimezoneAndTime(
  timestamp,
  tz = DEFAULT_TIMEZONE
) {
  if (!timestamp) return "";
  try {
    const utcTimestamp = parseDashboardTimestamp(timestamp);
    if (!utcTimestamp) return '';
    // Validate timezone and fall back to default if invalid
    const validTz = isCalendarDate(timestamp) ? 'UTC' : getValidTimezone(tz);
    const date = dayjs(utcTimestamp).tz(validTz);
    return date.format("MM-DD-YYYY, h:mm a");
  } catch (error) {
    console.error("Error formatting timestamp with timezone and time:", error);
    return "";
  }
}

/**
 * Format a UTC timestamp for CSV export with time and timezone offset
 * @param {string} timestamp - UTC timestamp (e.g., "2025-12-17T14:23:00.000Z")
 * @param {string} tz - IANA timezone identifier (e.g., "America/Chicago", "Europe/Madrid")
 * @returns {string} Formatted date string with time and timezone offset (MM-DD-YYYY, h:mm a (UTC-6))
 */
export function formatTimestampForExport(
  timestamp,
  tz = DEFAULT_TIMEZONE
) {
  if (!timestamp) return "";
  try {
    const utcTimestamp = parseDashboardTimestamp(timestamp);
    if (!utcTimestamp) return '';
    // Validate timezone and fall back to default if invalid
    const validTz = isCalendarDate(timestamp) ? 'UTC' : getValidTimezone(tz);
    const date = dayjs(utcTimestamp).tz(validTz);
    const offset = getTimezoneOffset(validTz, utcTimestamp);
    return `${date.format("MM-DD-YYYY, h:mm a")}${offset ? ` (${offset})` : ""}`;
  } catch (error) {
    console.error("Error formatting timestamp for export:", error);
    return "";
  }
}

/**
 * Format a UTC timestamp as a date-only value WITHOUT timezone conversion.
 * Use this for fields like Stripe's arrival_date which represent a calendar date
 * (stored as midnight UTC), NOT a precise moment in time. Converting these to
 * a local timezone would shift the date backward (e.g., March 5 00:00 UTC
 * becomes March 4 in US timezones).
 *
 * @param {string|null} timestamp - UTC timestamp string (e.g., "2026-03-05 00:00:00.000000")
 * @param {string} format - Output format (default: "MM-DD-YYYY")
 * @returns {string} Formatted date string in UTC (no timezone shift)
 */
export function formatUTCDateOnly(timestamp, format = "MM-DD-YYYY") {
  if (!timestamp) return "";
  try {
    const utcTimestamp = parseDashboardTimestamp(timestamp);
    if (!utcTimestamp) return '';
    // Use dayjs.utc() to parse and format in UTC — no timezone shift
    const parsed = dayjs.utc(utcTimestamp);
    if (!parsed.isValid()) return "";
    return parsed.format(format);
  } catch (error) {
    console.error("Error formatting UTC date-only timestamp:", error);
    return "";
  }
}

/**
 * Format a Unix timestamp (seconds) as a date-only value WITHOUT timezone conversion.
 * Same as formatUTCDateOnly but accepts a numeric Unix timestamp instead of a string.
 *
 * @param {number|null} unixSeconds - Unix timestamp in seconds
 * @param {string} format - Output format (default: "MM-DD-YYYY")
 * @returns {string} Formatted date string in UTC (no timezone shift)
 */
export function formatUnixAsUTCDateOnly(unixSeconds, format = "MM-DD-YYYY") {
  if (unixSeconds == null || !isFinite(unixSeconds)) return "";
  try {
    const parsed = dayjs.utc(unixSeconds * 1000);
    if (!parsed.isValid()) return "";
    return parsed.format(format);
  } catch (error) {
    console.error("Error formatting Unix timestamp as UTC date:", error);
    return "";
  }
}

export function formatTimestamp(timestamp) {
  // convert timestamps to an excel-friendly format
  // need the if statement because safari cant use Date class to parse DD-MM-YYYY formats
  if (timestamp.includes(":")) {
    const date = new Date(timestamp);
    return date.toISOString();
  }
  const date = dayjs(timestamp, "MM-DD-YYYY");
  return date.toISOString();
}

export function formatTimestampToDate(timestamp) {
  // convert timestamps to an excel-friendly format of just the date
  // need the if statement because safari cant use Date class to parse DD-MM-YYYY formats
  if (timestamp.includes(":")) {
    const date = new Date(timestamp);
    return date.toISOString().split("T")[0];
  }
  const date = dayjs(timestamp, "MM-DD-YYYY");
  return date.toISOString().split("T")[0];
}

export function dynamoDBTimestampToDate(timestamp) {
  // Assuming the timestamp is in UTC and needs to be converted to local timezone
  const date = new Date(timestamp + "Z"); // Append 'Z' to indicate UTC
  return (
    `${date.getMonth() + 1}`.padStart(2, "0") +
    "-" +
    `${date.getDate()}`.padStart(2, "0") +
    "-" +
    date.getFullYear()
  );
}

/**
 * @deprecated Use formatTimestampWithTimezone() instead for consistent timezone handling.
 * This function uses the browser's local timezone which creates inconsistency
 * when merchants have configured a specific timezone in their settings.
 *
 * Format 2024-09-05T23:13:51.189117Z to local time, and then format it as MM-DD-YYYY
 */
export function formatTimestampToLocal(timestamp) {
  console.warn(
    'formatTimestampToLocal is deprecated. Use formatTimestampWithTimezone(timestamp, merchantTimezone) instead.'
  );
  const date = new Date(timestamp);
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`; // Changed format to MM-DD-YYYY
}

function getDateRangeBoundaryInTimezone(dateValue, tz, endOfDay = false) {
  const parsedDate = new Date(dateValue);
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const time = endOfDay ? "23:59:59.999" : "00:00:00.000";
  const zonedDate = dayjs.tz(`${year}-${month}-${day}T${time}`, getValidTimezone(tz));

  return zonedDate.toISOString();
}

export function formatDateRockset(date, timezone) {
  if (date == null) {
    return { startDate: "", endDate: "" };
  }

  const hasRange = date.includes(" - ");

  if (timezone) {
    if (hasRange) {
      const dates = date.split(" - ");
      return {
        startDate: getDateRangeBoundaryInTimezone(dates[0], timezone),
        endDate: getDateRangeBoundaryInTimezone(dates[1], timezone, true),
      };
    }

    return {
      startDate: getDateRangeBoundaryInTimezone(date, timezone),
      endDate: getDateRangeBoundaryInTimezone(date, timezone, true),
    };
  }

  // Check if the date includes a range (indicated by ' - ')
  if (hasRange) {
    const dates = date.split(" - ");
    const startDate = new Date(dates[0]);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(dates[1]);
    endDate.setHours(23, 59, 59, 999);

    return {
      startDate: startDate.toISOString().replace("Z", "000Z"),
      endDate: endDate.toISOString().replace("Z", "999Z"),
    };
  } else {
    const singleDate = new Date(date);
    singleDate.setHours(23, 59, 59, 999);
    const endDate = singleDate.toISOString().replace("Z", "999Z");
    singleDate.setHours(0, 0, 0, 0);
    const startDate = singleDate.toISOString().replace("Z", "000Z");
    return {
      startDate: startDate,
      endDate: endDate,
    };
  }
}
